import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {PrintOptions, PrintTab, PrintTabOption} from '../../common/components/buttons/print-options.helper';
import {StudentBehaviorInterventionsComponent} from '../behaviorInterventions/student.behaviorinterventions.component';
import {StudentMeetingsComponent} from '../meeting/student.meetings.component';
import {StudentObservationsComponent} from '../observations/student.observations.component';
import {StudentOtherFactorsComponent} from '../otherFactors/student.otherfactors.component';
import {StudentCommunicationComponent} from '../communication/student.communication.component';
import {StudentIntvLogComponent} from '../interventionLog/student.intvlog.component';
import {StudentAchievementsComponent} from '../achievements/student-achievements.component';
import {StudentRunningRecordComponent} from '../runningRecord/student-runningrecord.component';
import {StudentService} from '../../services/student.service';
import {TitleService} from '../../services/title.service';
import {StudentAppropriateInstructionComponent} from '../appropriateInstructions/student-appropriate-instruction.component';
import {UserService} from '../../services/user.service';
import {StudentConstModules} from '../../constants/modules';
import {environment} from '../../../environments/environment';
import {StudentDto} from '@sidr/dto-types';

@Component({
  selector: 'app-student-view',
  templateUrl: './studentPage.component.html',
  styleUrls: ['./studentPage.component.scss']
})
class StudentPageComponent implements OnInit {
  studentId: string;
  districtId: string;
  loading: boolean;
  student: StudentDto;
  printOptions: PrintOptions;
  selected: string;
  subselect: string;
  showPhoto = false;
  isMenuCollapsed = true;

  @ViewChild('achievements') public Achievements: StudentAchievementsComponent;
  @ViewChild(StudentAppropriateInstructionComponent) public ApprInst: StudentAppropriateInstructionComponent;
  @ViewChild(StudentCommunicationComponent) public Communication: StudentCommunicationComponent;
  @ViewChild('runningrecord') public RunningRecord: StudentRunningRecordComponent;
  @ViewChild('bhvrintv') public InterventionBehaviors: StudentBehaviorInterventionsComponent;
  @ViewChild(StudentIntvLogComponent) public InterventionLogs: StudentIntvLogComponent;
  @ViewChild(StudentMeetingsComponent) public Meetings: StudentMeetingsComponent;
  @ViewChild(StudentObservationsComponent) public Observations: StudentObservationsComponent;
  @ViewChild('otherfactors') public OtherFactors: StudentOtherFactorsComponent;


  public moduleEnabled(name: string): boolean {
    const realName: string = StudentConstModules[name];
    return this.userService.hasModuleEnabled(realName ?? name) || environment.roleModuleDisabled;
  }

  items: any = {
    information : StudentConstModules['information'],
    assessments : StudentConstModules['assessments'],
    achievements : StudentConstModules['achievements'],
    attendance : StudentConstModules['attendance'],
    attendanceperiod : StudentConstModules['attendance-by-period'],
    behaviors : StudentConstModules['historical-behaviors'],
    otherfactors : StudentConstModules['other-factors'],
    communication : StudentConstModules['communication'],
    initialcontact : StudentConstModules['parent-notification'],
    runningrecord : StudentConstModules['running-record'],
    bhvrintv : StudentConstModules['behavior-intervention'],
    intvlog : StudentConstModules['intervention-logs'],
    irip : StudentConstModules['irip'],
    observations : StudentConstModules['observational-data'],
    meetings : StudentConstModules['meetings'],
  }

  constructor(private route: ActivatedRoute,
              public studentService: StudentService,
              private userService: UserService,
              private titleService: TitleService) {
    this.loading = false;
    this.student = {} as StudentDto;
    this.printOptions = new PrintOptions();
    this.studentId = '';
    this.districtId = '';

    const displayTab = new PrintTab('Print Options');
    displayTab.options.push(new PrintTabOption('Section Headers', 'display.headers', 'checkbox', true));
    displayTab.options.push(new PrintTabOption('Student Information', this.items.information, 'checkbox', true));
    displayTab.options.push(new PrintTabOption('Student Tab', 'student/student', 'checkbox', true));
    displayTab.options.push(new PrintTabOption('Staff Tab', 'student/staff', 'checkbox', true));
    displayTab.options.push(new PrintTabOption('Meetings Tab', this.items.meetings, 'checkbox', true));

    const studentTab = new PrintTab('Student Tab', 'student/student');
    studentTab.options.push(new PrintTabOption('Achievements', this.items.achievements, 'checkbox', true));
    studentTab.options.push(new PrintTabOption('Assessments', this.items.assessments, 'checkbox', true));
    studentTab.options.push(new PrintTabOption('Attendance', this.items.attendance, 'checkbox', true));
    studentTab.options.push(new PrintTabOption('Attendance by Period', this.items.attendanceperiod, 'checkbox', true));
    studentTab.options.push(new PrintTabOption('Historical Behaviors', this.items.behaviors, 'checkbox', true));
    studentTab.options.push(new PrintTabOption('Other Factors', this.items.otherfactors, 'checkbox', true));

    const staffTab = new PrintTab('Staff Tab', 'student/staff');
    staffTab.options.push(new PrintTabOption('Communication', this.items.communication, 'checkbox', true));
    staffTab.options.push(new PrintTabOption('Parent Notification Process', this.items.initialcontact, 'checkbox', true));
    staffTab.options.push(new PrintTabOption('Running Record', this.items.runningrecord, 'checkbox', true));
    staffTab.options.push(new PrintTabOption('Behavior Interventions', this.items.bhvrintv, 'checkbox', true));
    staffTab.options.push(new PrintTabOption('Intervention Logs', this.items.intvlog, 'checkbox', true));
    staffTab.options.push(new PrintTabOption('IRIP', this.items.irip, 'checkbox', true));
    staffTab.options.push(new PrintTabOption('Observational Data', this.items.observations, 'checkbox', true));
    // staffTab.options.push(new PrintTabOption('Appropriate Instruction', 'staff.appr_inst', 'checkbox', true));


    this.printOptions.tabs.push(displayTab);
    this.printOptions.tabs.push(studentTab);
    this.printOptions.tabs.push(staffTab);
  }

  get token(): string {
    return localStorage.getItem('jwt') || '';
  }

  select(select: string, subselect = '') {
    this.selected = select;
    this.subselect = subselect;
  }

  getModuleClass(item: string): string {
    let classes: string[] = ['student_page'];

    if(this.display(item)) {
      classes.push('d-block')
    } else {
      classes.push('d-none');
    }

    let isInformation = item.indexOf('student/information') > -1;
    let isStudent = item.indexOf('student/student') > -1;
    let isStaff = item.indexOf('student/staff') > -1;
    let isMeetings = item.indexOf('student/meetings') > -1;

    // IRIP has it's own print logic, don't print here
    let isIrip = item.indexOf(this.items.irip) > -1;

    if(this.printOptions.getValue(item) && !isIrip) {
      if(isInformation && this.printOptions.getValue('student/information')) {
        classes.push('d-print-block');
      } else if (isStudent && this.printOptions.getValue('student/student')) {
        classes.push('d-print-block');
      } else if (isStaff && this.printOptions.getValue('student/staff')) {
        classes.push('d-print-block');
      } else if (isMeetings && this.printOptions.getValue(this.items.meetings)) {
        classes.push('d-print-block');
      } else {
        classes.push('d-print-none');
      }
    } else {
      classes.push('d-print-none');
    }

    return classes.join(' ');
  }

  display(item: string): boolean {
    return this.selected === item;
  }

  loadStudent() {
    this.loading = true;
    this.studentService.get(this.districtId, this.studentId).subscribe(resp => {
      this.titleService.setTitle(`${resp.firstName} ${resp.lastName}`);
      this.select(this.items.information);
      this.student = resp;
      this.showPhoto = true;
      this.loading = false;
    });
  }

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.showPhoto = false;
      this.studentId = params['student_id'];
      this.districtId = params['district_id'];
      this.loadStudent();
    });
  }
}

export default StudentPageComponent;
